import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add, Edit, Delete, Chat } from '@mui/icons-material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import apiService from '../services/apiService';

const QuickChatPage = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [tabValue, setTabValue] = useState(0); // 0 = All, 1 = Driver, 2 = Passenger

  useEffect(() => {
    fetchQuickChatMessages();
  }, []);

  const fetchQuickChatMessages = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.getQuickChatMessages();

      if (response.success) {
        setMessages(response.data);
      } else {
        setError(response.message || 'Failed to load quick chat messages');
      }
    } catch (err) {
      console.error('Quick chat messages error:', err);
      setError('Failed to load quick chat messages');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditMode(false);
    setSelectedMessage(null);
    setOpenDialog(true);
  };

  const handleEdit = (message) => {
    setEditMode(true);
    setSelectedMessage(message);
    setOpenDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this quick chat message?')) {
      return;
    }

    try {
      const response = await apiService.deleteQuickChatMessage(id);

      if (response.success) {
        alert('Quick chat message deleted successfully');
        fetchQuickChatMessages();
      } else {
        alert(response.message || 'Failed to delete quick chat message');
      }
    } catch (err) {
      alert('Failed to delete quick chat message');
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      let response;
      if (editMode) {
        response = await apiService.updateQuickChatMessage(selectedMessage.id, values);
      } else {
        response = await apiService.addQuickChatMessage(values);
      }

      if (response.success) {
        alert(`Quick chat message ${editMode ? 'updated' : 'added'} successfully`);
        setOpenDialog(false);
        fetchQuickChatMessages();
      } else {
        alert(response.message || 'Operation failed');
      }
    } catch (err) {
      console.error('Quick chat message operation error:', err);
      alert('Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const validationSchema = Yup.object({
    message: Yup.string().required('Message is required'),
    type: Yup.string().oneOf(['driver', 'passenger']).required('Type is required'),
  });

  const getFilteredMessages = () => {
    if (tabValue === 0) return messages;
    if (tabValue === 1) return messages.filter((m) => m.type === 'driver');
    if (tabValue === 2) return messages.filter((m) => m.type === 'passenger');
    return messages;
  };

  const columns = [
    {
      field: 'id',
      headerName: 'ID',
      width: 80,
    },
    {
      field: 'message',
      headerName: 'Message',
      flex: 1,
      minWidth: 300,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chat color="primary" fontSize="small" />
          <Typography variant="body2" fontWeight={500}>
            {params.row.message}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 150,
      renderCell: (params) => (
        <Chip
          label={params.row.type === 'driver' ? 'Driver' : 'Passenger'}
          color={params.row.type === 'driver' ? 'primary' : 'secondary'}
          size="small"
          sx={{ textTransform: 'capitalize' }}
        />
      ),
    },
    {
      field: 'sort_order',
      headerName: 'Order',
      width: 100,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.row.status === 'active' ? 'Active' : 'Inactive'}
          color={params.row.status === 'active' ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton size="small" onClick={() => handleEdit(params.row)} color="primary">
            <Edit fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => handleDelete(params.row.id)}>
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Manage Quick Chat
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage quick chat messages for drivers and passengers
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={handleAdd}>
          Add Quick Chat Message
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
            sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label={`All (${messages.length})`} />
            <Tab label={`Driver (${messages.filter((m) => m.type === 'driver').length})`} />
            <Tab label={`Passenger (${messages.filter((m) => m.type === 'passenger').length})`} />
          </Tabs>

          <DataGrid
            rows={getFilteredMessages()}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            autoHeight
            loading={loading}
            sx={{
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
            }}
          />
        </CardContent>
      </Card>

      {/* Add/Edit Quick Chat Message Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editMode ? 'Edit' : 'Add'} Quick Chat Message</DialogTitle>
        <Formik
          initialValues={{
            message: selectedMessage?.message || '',
            type: selectedMessage?.type || '',
            sort_order: selectedMessage?.sort_order || 0,
            status: selectedMessage?.status || 'active',
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
            <Form>
              <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <TextField
                    fullWidth
                    name="message"
                    label="Message"
                    value={values.message}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.message && Boolean(errors.message)}
                    helperText={touched.message && errors.message}
                    placeholder="Enter quick chat message"
                    multiline
                    rows={2}
                  />

                  <FormControl fullWidth error={touched.type && Boolean(errors.type)}>
                    <InputLabel>Type</InputLabel>
                    <Select
                      name="type"
                      value={values.type}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      label="Type"
                    >
                      <MenuItem value="driver">Driver</MenuItem>
                      <MenuItem value="passenger">Passenger</MenuItem>
                    </Select>
                    {touched.type && errors.type && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                        {errors.type}
                      </Typography>
                    )}
                  </FormControl>

                  <TextField
                    fullWidth
                    name="sort_order"
                    label="Sort Order"
                    type="number"
                    value={values.sort_order}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Enter sort order (0, 1, 2...)"
                  />

                  {editMode && (
                    <FormControl fullWidth>
                      <InputLabel>Status</InputLabel>
                      <Select
                        name="status"
                        value={values.status}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        label="Status"
                      >
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="inactive">Inactive</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                  sx={{
                    bgcolor: '#FFC107',
                    color: '#000',
                    '&:hover': {
                      bgcolor: '#FFD54F',
                    },
                  }}
                >
                  {editMode ? 'Update' : 'Save'}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </Box>
  );
};

export default QuickChatPage;
